import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	Optional,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { TWO_FACTOR_AUTHENTICATION_DIALOG_ID, twoFactorAuthVerificationDialogFactory } from "@obg/common.verification";
import { updateQueryStringParameters } from "@obg/global.evo";
import { LoginFlowDialogService, SgaVerificationService } from "@obg/mat.ui.common";
import { DialogService } from "@obg/mat.ui.core";
import { HampiService, TwoFactorAuthFlow } from "@obg/ngx.common";
import {
	AuthenticationStatus,
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	CURRENT_LOGIN_SOURCE,
	CurrentLoginSource,
	CUSTOMER_MANAGEMENT_MESSAGE_CODES,
	CustomerManagementSelector,
	DepositLimitConfigService,
	DepositLimitRequiredService,
	DialogStatusSelector,
	Jurisdiction,
	JurisdictionAction,
	JurisdictionSelector,
	LoginSource,
	MessageService,
	ProductType,
	ResponsibleGamingConfigService,
	SessionAction,
	SessionLimitRequiredService,
	SessionSelector,
	SessionConfigService,
} from "@obg/ngx.core";
import { PlatformService } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { BehaviorSubject, merge, Observable, of, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, pairwise, switchMap, take, tap } from "rxjs/operators";

import { LoginContainer } from "../container/login.container";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";
import { LoginUiConfigService } from "../login.config";
import { LoginTab } from "../login.model";

import { CommonPokerLoginCallbackQueryParams, CommonPokerLoginQueryParams, CurrentStep } from "./poker-login.model";

@Component({
	selector: "obg-m-common-poker-login-container",
	templateUrl: "./poker-login.container.html",
	styleUrls: ["poker-login.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	preserveWhitespaces: false,
})
export class PokerLoginContainer implements OnInit, OnDestroy {
	@ViewChild("loginContainer", { static: false }) loginContainer: LoginContainer | undefined;
	@HostBinding("class.obg-m-common-poker-login") hostCssClass = true;

	data$$: Subscription | undefined;

	sgaMessages$ = this.loginUiFlowService.sgaLoginAlert$().pipe(ignoreNil());

	initialJurisdiction: Jurisdiction | undefined;
	customerJurisdiction: Jurisdiction | undefined;
	pegaJurisdiction: boolean | undefined;

	disallowedRedirection = false;
	disallowLogin = false;
	pokerLoginProfileCompletenessPageEnabled = false;
	isTwoFactorAuth = false;

	authToken: string | undefined;

	readonly showLicenseLogo$ = this.store.pipe(
		select(JurisdictionSelector.get),
		ignoreNil(),
		map((jurisdiction) => jurisdiction === Jurisdiction.Mga)
	);

	readonly hideEmailPasswordLoginForDga$ = this.store
		.select<{ hideEmailPasswordLoginForDga: boolean } | undefined>(RouteSelector.getData)
		.pipe(map((params) => params?.hideEmailPasswordLoginForDga));

	readonly isLoading$ = new BehaviorSubject(false);
	readonly currentStep$ = new BehaviorSubject(CurrentStep.Login);
	readonly stepLogin = CurrentStep.Login;
	readonly stepSgaVerification = CurrentStep.SgaVerification;
	readonly stepTC = CurrentStep.Tc;
	readonly stepPokerMigration = CurrentStep.PokerMigration;
	readonly stepProfileCompleteness = CurrentStep.ProfileCompleteness;
	readonly stepSgaMessage = CurrentStep.SgaMessage;
	readonly stepJurisdictionMismatch = CurrentStep.JurisdictionMismatch;
	readonly hideSgaLogin$ = this.store
		.select<CommonPokerLoginQueryParams | undefined>(RouteSelector.getQueryParams)
		.pipe(map((params) => !!params?.jurisdiction && params.jurisdiction !== Jurisdiction.Sga));
	private readonly sgaMessageClose$ = new Subject<void>();

	constructor(
		private store: Store<CoreState>,
		private jurisdictionAction: JurisdictionAction,
		private loginUiFlowService: LoginUiFlowService,
		private platformService: PlatformService,
		private messageService: MessageService,
		private dialogSelector: DialogStatusSelector,
		private depositLimitRequiredService: DepositLimitRequiredService,
		private responsibleGamingConfig: ResponsibleGamingConfigService,
		private sessionLimitRequiredService: SessionLimitRequiredService,
		private depositLimitConfig: DepositLimitConfigService,
		private hampiService: HampiService,
		private sgaVerificationService: SgaVerificationService,
		private loginUiConfigService: LoginUiConfigService,
		private sessionSelector: SessionSelector,
		private dialogService: DialogService,
		private loginFlowDialogService: LoginFlowDialogService,
		private sessionAction: SessionAction,
		private sessionConfigService: SessionConfigService,
		@Optional() @Inject(CURRENT_LOGIN_SOURCE) loginSource?: CurrentLoginSource
	) {
		if (loginSource) {
			loginSource.loginSource = LoginSource.IPoker;
		}
		this.disallowedRedirection = this.loginUiConfigService.getPokerLoginRedirectionDisallowed();
		this.disallowLogin =
			this.sessionConfigService.isCountryBlockingEnabled() ||
			this.sessionConfigService.isPokerDisallowedLoginEnabled();
		this.pokerLoginProfileCompletenessPageEnabled =
			this.loginUiConfigService.getPokerLoginProfileCompletenessPageEnabled();
	}

	ngOnInit() {
		if (this.platformService.isServer) {
			return;
		}

		this.isLoading$.next(true);

		this.store.dispatch(this.sessionAction.logout({ shouldSkipNotification: true }));

		const jurisdictionVerification$ = this.store.select(CustomerManagementSelector.getCustomer).pipe(
			ignoreNil(),
			filter((customer) => {
				if (
					this.initialJurisdiction &&
					customer.jurisdiction &&
					customer.jurisdiction !== this.initialJurisdiction
				) {
					this.pegaJurisdiction =
						this.initialJurisdiction === Jurisdiction.Pega || customer.jurisdiction === Jurisdiction.Pega;
					this.customerJurisdiction = customer.jurisdiction;
					this.currentStep$.next(CurrentStep.JurisdictionMismatch);
					this.isLoading$.next(false);
					return false;
				}

				return true;
			})
		);

		const login$ = this.store.select(AuthSelector.getAuthenticationStatus).pipe(
			tap((authStatus) => {
				if (authStatus !== AuthenticationStatus.NotAuthenticated || this.pegaJurisdiction) {
					return;
				}

				this.currentStep$.next(CurrentStep.Login);
				this.isLoading$.next(false);
			}),
			pairwise(),
			filter(
				([prev, next]) =>
					prev === AuthenticationStatus.NotAuthenticated && next === AuthenticationStatus.Authenticated
			),
			tap(() => {
				// if user has 2FA and login was successful after that, we close the dialog before navigation
				if (this.isTwoFactorAuth) {
					this.loginFlowDialogService.closeTwoFactorAuthDialog();
					this.isTwoFactorAuth = false;
				}
			}),
			switchMap(() => jurisdictionVerification$),
			switchMap(() => this.handleDepositLimit$()),
			switchMap(() => this.handleSessionLimit$()),
			switchMap(() => this.handleSgaMessage$()),
			switchMap(() => this.handleHampiBlock$()),
			switchMap(() => this.navigateToCallbackUrl$())
		);

		const authToken$ = this.store.select(this.sessionSelector.getAuthenticationToken()).pipe(
			ignoreNil(),
			tap((token) => {
				this.authToken = token;
			})
		);

		const customerMigration$ = merge(
			this.loginUiFlowService.detectLatestTCandPP$(),
			this.loginUiFlowService.detectProfileNotCompleted$()
		).pipe(
			filter((data) => !!data),
			tap((data) => {
				if (data.jurisdiction) {
					this.customerJurisdiction = data.jurisdiction as Jurisdiction;
				}
				this.pokerLoginProfileCompletenessPageEnabled
					? this.currentStep$.next(CurrentStep.ProfileCompleteness)
					: this.currentStep$.next(CurrentStep.PokerMigration);
				this.isLoading$.next(false);
			})
		);

		const updatedTC$ = this.loginUiFlowService.detectTermsAndConditions$().pipe(
			tap(() => {
				this.currentStep$.next(CurrentStep.Tc);
				this.isLoading$.next(false);
			})
		);

		const twoFactorAuth$ = this.loginUiFlowService.detectTwoFactorAuth$().pipe(
			tap(() => {
				this.isTwoFactorAuth = true;
				const open = () => {
					this.dialogService.openComponent(
						twoFactorAuthVerificationDialogFactory({
							authFlow: TwoFactorAuthFlow.LoginFlow,
						}),
						{
							id: TWO_FACTOR_AUTHENTICATION_DIALOG_ID,
							disableClose: true,
						}
					);
				};
				this.loginFlowDialogService.openDialog(open, TWO_FACTOR_AUTHENTICATION_DIALOG_ID);
				this.isLoading$.next(false);
			})
		);

		const sgaVerification$ = this.sgaVerificationService.sgaVerification$().pipe(
			distinctUntilChanged(),
			// should not open BankID verification if already doing BankID login
			filter(() => !this.loginContainer || this.loginContainer.selectedTab !== LoginTab.BankID),
			tap(() => {
				this.currentStep$.next(CurrentStep.SgaVerification);
				this.isLoading$.next(false);
			})
		);

		const jurisdictionFreeze$ = this.store
			.select<CommonPokerLoginQueryParams | undefined>(RouteSelector.getQueryParams)
			.pipe(
				ignoreNil(),
				filter((params) => !!params.jurisdiction),
				take(1),
				tap((params) => {
					const jurisdictionValue = Array.isArray(params.jurisdiction)
						? params.jurisdiction[0]!
						: params.jurisdiction!;
					this.initialJurisdiction = jurisdictionValue.split(",")[0] as Jurisdiction;
					this.store.dispatch(this.jurisdictionAction.set(this.initialJurisdiction));
				})
			);

		const invalidJurisdiction$ = this.messageService
			.consume$({
				messageCode: [CUSTOMER_MANAGEMENT_MESSAGE_CODES.invalidJurisdiction],
			})
			.pipe(
				map((messages) => messages[0]),
				tap(() => this.endSession())
			);

		this.data$$ = merge(
			authToken$,
			jurisdictionFreeze$,
			invalidJurisdiction$,
			login$,
			updatedTC$,
			customerMigration$,
			twoFactorAuth$,
			sgaVerification$,
			this.loginUiFlowService.authorize$()
		).subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	onSgaDismiss() {
		this.isLoading$.next(true);
		this.sgaMessageClose$.next();
	}

	endSession() {
		this.store.dispatch(this.sessionAction.logout());
		if (this.initialJurisdiction) {
			this.jurisdictionAction.set(this.initialJurisdiction);
		}
		if (
			this.currentStep$.value === CurrentStep.PokerMigration ||
			this.currentStep$.value === CurrentStep.ProfileCompleteness
		) {
			this.currentStep$.next(CurrentStep.Login);
		}
	}

	private navigateToCallbackUrl$() {
		return this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			filter((route) => route.queryParams?.callbackUrl),
			take(1),
			tap((route) => {
				const queryParams: CommonPokerLoginCallbackQueryParams & Record<string, string> = {
					jurisdiction: this.initialJurisdiction ?? Jurisdiction.Mga,
					referrer: encodeURIComponent(route.externalUrl),
				};

				const callbackUrl = updateQueryStringParameters(route.queryParams!.callbackUrl, queryParams);

				this.store.dispatch(RouteAction.navigateByUrl(callbackUrl, { replaceUrl: true }));
			})
		);
	}

	private handleDepositLimit$(): Observable<boolean> {
		return this.depositLimitConfig.getIsDepositLimitRequired$().pipe(
			switchMap((required) =>
				required
					? this.store.select(this.dialogSelector.hasOpenDialogs()).pipe(
							filter((openDialogs) => !openDialogs),
							take(1),
							switchMap(() => this.depositLimitRequiredService.shouldOpenDepositLimit$()),
							map((shouldOpen) => {
								if (shouldOpen) {
									this.store.dispatch(
										RouteAction.navigate(CORE_ROUTE.responsibleGaming.depositLimitDialog)
									);
									this.isLoading$.next(false);
								}
								return !shouldOpen;
							})
						)
					: of(true)
			),
			filter((handled: boolean) => handled),
			take(1)
		);
	}

	private handleSessionLimit$(): Observable<boolean> {
		return this.responsibleGamingConfig.getSessionLimitIsRequired$().pipe(
			switchMap((required) =>
				required
					? this.store.select(this.dialogSelector.hasOpenDialogs()).pipe(
							filter((openDialogs) => !openDialogs),
							take(1),
							switchMap(() => this.sessionLimitRequiredService.shouldOpenSessionLimit$()),
							map((shouldOpen) => {
								if (shouldOpen) {
									this.store.dispatch(
										RouteAction.navigate(CORE_ROUTE.responsibleGaming.sessionLimitDialog)
									);
									this.isLoading$.next(false);
								}
								return !shouldOpen;
							})
						)
					: of(true)
			),
			filter((handled: boolean) => handled),
			take(1)
		);
	}

	private handleSgaMessage$(): Observable<void> {
		return this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			switchMap((jurisdiction) => {
				if (jurisdiction !== Jurisdiction.Sga) {
					return of(undefined);
				}
				this.currentStep$.next(CurrentStep.SgaMessage);
				this.isLoading$.next(false);
				return this.sgaMessageClose$;
			})
		);
	}

	private handleHampiBlock$(): Observable<boolean> {
		return this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			switchMap((jurisdiction) =>
				jurisdiction === Jurisdiction.Mta
					? this.hampiService.isProductAllowed$(ProductType.Poker, false, true).pipe(
							tap((isProductAllowed) => {
								if (!isProductAllowed) {
									this.endSession();
								}
							})
						)
					: of(true)
			),
			filter((handled: boolean) => handled),
			take(1)
		);
	}
}
